import { useState, useRef, useCallback, useEffect, useMemo } from "react";

interface UsePdfViewerOptions {
  storageKey?: string;
  defaultScale?: number;
  minScale?: number;
  maxScale?: number;
  zoomStep?: number;
  onPageChange?: (page: number) => void;
  /** Current tool name — when "pan", mouse-down on the container starts panning. */
  selectedTool?: string | null;
}

interface PdfDimensions {
  width: number;
  height: number;
}

interface UsePdfViewerResult {
  // State
  numPages: number;
  currentPage: number;
  pdfDimensions: PdfDimensions;
  pageReady: boolean;
  scale: number;
  isPanning: boolean;
  isSpaceHeld: boolean;

  // Refs
  containerRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.RefObject<HTMLDivElement>;

  // PDF callbacks
  onDocumentLoadSuccess: (result: { numPages: number }) => void;
  /** For react-pdf Page onRenderSuccess (reads canvas size). */
  onPageRenderSuccess: () => void;
  /** For react-pdf Page onLoadSuccess (reads viewport). Prefer this when available. */
  onPageLoadSuccess: (page: { getViewport: (opts: { scale: number }) => { width: number; height: number } }) => void;

  /**
   * Container mouse-down handler that starts panning when Space is held or
   * `selectedTool === "pan"`. Returns `true` if panning was started so the
   * consumer can skip its own logic. Also skips `.react-draggable` targets
   * and non-left-clicks.
   */
  handleContainerMouseDown: (e: React.MouseEvent) => boolean;
  handleContainerMouseMove: (e: React.MouseEvent) => void;
  handleContainerMouseEnd: () => void;

  /** CSS cursor class for the scroll container based on pan state. */
  panCursorClass: string | undefined;

  // Zoom
  zoomIn: () => void;
  zoomOut: () => void;
  zoomFit: () => void;
  setScale: (scale: number) => void;

  // Page navigation
  prevPage: () => void;
  nextPage: () => void;
  goToPage: (page: number) => void;
  setPageReady: (ready: boolean) => void;

  // Utility
  getCanvasCoords: (e: React.MouseEvent | MouseEvent) => { x: number; y: number };
}

export function usePdfViewer(options: UsePdfViewerOptions = {}): UsePdfViewerResult {
  const {
    storageKey = "formDesignerZoom",
    defaultScale = 1,
    minScale = 0.25,
    maxScale = 3,
    zoomStep = 1.25,
    onPageChange,
    selectedTool = null,
  } = options;

  // State
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pdfDimensions, setPdfDimensions] = useState<PdfDimensions>({ width: 0, height: 0 });
  const [pageReady, setPageReady] = useState(false);
  const [scale, setScaleRaw] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? parseFloat(saved) : defaultScale;
  });
  const [isPanning, setIsPanning] = useState(false);
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);

  // Refs
  const containerRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const canvasRef = useRef<HTMLDivElement>(null) as React.RefObject<HTMLDivElement>;
  const panStartRef = useRef<{ x: number; y: number; scrollLeft: number; scrollTop: number } | null>(null);

  // Scale with persistence
  const persistScale = useCallback(
    (val: number) => {
      const clamped = Math.min(Math.max(val, minScale), maxScale);
      setScaleRaw(clamped);
      localStorage.setItem(storageKey, String(clamped));
    },
    [storageKey, minScale, maxScale],
  );

  // PDF load handlers
  const onDocumentLoadSuccess = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
  }, []);

  const onPageRenderSuccess = useCallback(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current.querySelector("canvas");
      if (canvas) {
        setPdfDimensions({
          width: canvas.width / scale,
          height: canvas.height / scale,
        });
      }
    }
    setPageReady(true);
  }, [scale]);

  const onPageLoadSuccess = useCallback(
    (page: { getViewport: (opts: { scale: number }) => { width: number; height: number } }) => {
      const viewport = page.getViewport({ scale: 1 });
      setPdfDimensions({ width: viewport.width, height: viewport.height });
    },
    [],
  );

  // Space key for panning
  useEffect(() => {
    const isInputFocused = () => {
      const el = document.activeElement;
      return (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement
      );
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === " " && !isInputFocused()) {
        e.preventDefault();
        if (!e.repeat) {
          setIsSpaceHeld(true);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key === " ") {
        e.preventDefault();
        setIsSpaceHeld(false);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Canvas coordinates helper
  const getCanvasCoords = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      if (!canvasRef.current) return { x: 0, y: 0 };
      const rect = canvasRef.current.getBoundingClientRect();
      return {
        x: (e.clientX - rect.left) / scale,
        y: (e.clientY - rect.top) / scale,
      };
    },
    [scale],
  );

  // Whether pan should activate on the next mouse-down
  const shouldPan = isSpaceHeld || selectedTool === "pan";

  // Pan cursor class for the container
  const panCursorClass = useMemo(
    () =>
      isPanning
        ? "cursor-grabbing"
        : shouldPan
          ? "cursor-grab"
          : undefined,
    [isPanning, shouldPan],
  );

  // Container mouse-down: start panning if conditions are met.
  // Returns true if panning was started.
  const handleContainerMouseDown = useCallback(
    (e: React.MouseEvent): boolean => {
      if (e.button !== 0) return false;

      // Don't intercept clicks on draggable fields
      const target = e.target as HTMLElement;
      if (target.closest(".react-draggable")) return false;

      if (!shouldPan || !containerRef.current) return false;

      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        scrollLeft: containerRef.current.scrollLeft,
        scrollTop: containerRef.current.scrollTop,
      };
      e.preventDefault();
      return true;
    },
    [shouldPan],
  );

  const handleContainerMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isPanning || !panStartRef.current || !containerRef.current) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      containerRef.current.scrollLeft = panStartRef.current.scrollLeft - dx;
      containerRef.current.scrollTop = panStartRef.current.scrollTop - dy;
    },
    [isPanning],
  );

  const handleContainerMouseEnd = useCallback(() => {
    setIsPanning(false);
    panStartRef.current = null;
  }, []);

  // Zoom
  const zoomIn = useCallback(
    () => persistScale(Math.min(scale * zoomStep, maxScale)),
    [scale, zoomStep, maxScale, persistScale],
  );

  const zoomOut = useCallback(
    () => persistScale(Math.max(scale / zoomStep, minScale)),
    [scale, zoomStep, minScale, persistScale],
  );

  const zoomFit = useCallback(() => {
    if (!containerRef.current || !pdfDimensions.width) return;
    const containerWidth = containerRef.current.clientWidth - 64;
    const fitScale = containerWidth / pdfDimensions.width;
    persistScale(fitScale);
  }, [pdfDimensions.width, persistScale]);

  // Page navigation
  const changePage = useCallback(
    (page: number) => {
      setPageReady(false);
      setCurrentPage(page);
      onPageChange?.(page);
    },
    [onPageChange],
  );

  const prevPage = useCallback(() => {
    if (currentPage <= 1) return;
    changePage(currentPage - 1);
  }, [currentPage, changePage]);

  const nextPage = useCallback(() => {
    if (currentPage >= numPages) return;
    changePage(currentPage + 1);
  }, [currentPage, numPages, changePage]);

  const goToPage = useCallback(
    (page: number) => {
      // Before the document reports its page count there is nowhere to go;
      // clamping to 0 pages would select page 0 and the viewer never renders.
      if (numPages < 1) return;
      const clamped = Math.min(Math.max(1, page), numPages);
      if (clamped !== currentPage) changePage(clamped);
    },
    [numPages, currentPage, changePage],
  );

  return {
    numPages,
    currentPage,
    pdfDimensions,
    pageReady,
    scale,
    isPanning,
    isSpaceHeld,
    containerRef,
    canvasRef,
    onDocumentLoadSuccess,
    onPageRenderSuccess,
    onPageLoadSuccess,
    handleContainerMouseDown,
    handleContainerMouseMove,
    handleContainerMouseEnd,
    panCursorClass,
    zoomIn,
    zoomOut,
    zoomFit,
    setScale: persistScale,
    prevPage,
    nextPage,
    goToPage,
    setPageReady,
    getCanvasCoords,
  };
}
